import React, { useEffect, useRef, useState } from 'react';
import { Sparkline, COLORS, prefersReducedMotion } from '../charts';
import './ambient.css';

// Shared kit for the Ambient Screen concept figures: a fixed 1040x585 design
// canvas scaled to the figure width, a tab strip that tours itself until the
// visitor clicks, device frames (panel / portable / glow / ceiling / phone)
// and the small dense primitives every layout is built from. Synthetic data
// only; the honesty chip sits in the figure foot.

export const W = 1040;
export const H = 585;

export function ScaledCanvas({ w = W, h = H, children }) {
  const ref = useRef(null);
  const [s, setS] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setS(e.contentRect.width / w));
    ro.observe(el);
    return () => ro.disconnect();
  }, [w]);
  return (
    <div ref={ref} className="as-canvas" style={{ height: Math.round(h * s) }}>
      <div className="as-canvas-in" style={{ width: w, height: h, transform: `scale(${s})` }}>{children}</div>
    </div>
  );
}

// tabs: [{ id, label, sub, note }]; children(tab, index, select) renders the
// canvas. The tour advances until the visitor picks a frame (tab, thumbnail,
// ‹ ›, or the hold button); ▶ resumes from the frame on screen. `frame`
// (0-based, optional) is the controlled form used by the page's ?fig=&frame=
// deep link so an agent can drive the carousel: each change selects + holds.
export function Tabbed({ tabs, interval = 3600, start = 1, h = H, children, extra, frame = null }) {
  const n = tabs.length;
  const [i, setI] = useState(() => (frame != null ? ((frame % n) + n) % n : prefersReducedMotion() ? Math.min(start, n - 1) : 0));
  const [held, setHeld] = useState(() => frame != null || prefersReducedMotion());
  useEffect(() => {
    if (held) return undefined;
    const t = setInterval(() => setI((v) => (v + 1) % n), interval);
    return () => clearInterval(t);
  }, [n, interval, held]);
  const select = (k) => { setI(((k % n) + n) % n); setHeld(true); };
  useEffect(() => { if (frame != null) select(frame); }, [frame]); // eslint-disable-line react-hooks/exhaustive-deps
  const tab = tabs[i];
  return (
    <div className="as-screen">
      <div className="as-tabs" role="tablist">
        {tabs.map((t, k) => (
          <button key={t.id} type="button" role="tab" aria-selected={k === i} className={k === i ? 'on' : ''} onClick={() => select(k)}>
            {t.label}{t.sub && <small>{t.sub}</small>}
          </button>
        ))}
        <span className="as-tabctl" role="group" aria-label="frame controls">
          <button type="button" onClick={() => select(i - 1)} aria-label="previous frame">‹</button>
          <button type="button" onClick={() => setHeld((v) => !v)} aria-pressed={held} title={held ? 'resume the tour' : 'hold this frame'}>
            {held ? '▶' : '❚❚'}
          </button>
          <button type="button" onClick={() => select(i + 1)} aria-label="next frame">›</button>
          <small>{i + 1} / {n}</small>
        </span>
      </div>
      <ScaledCanvas h={h}>{children(tab, i, select)}</ScaledCanvas>
      <div className="as-foot">
        <span className="as-foot-chip">synthetic data</span>
        <span>{tab.note}</span>
        {extra}
      </div>
    </div>
  );
}

// kind: panel | portable | glow | ceiling | phone | console
// source (glow/ceiling only): beam | pendant | mount | lamp — draws the
// projector and its light cone in the frame so the image never floats.
export function Frame({ kind = 'panel', label, scene, source, children, style, className = '' }) {
  return (
    <div className={`as-frame as-${kind} ${source ? `as-src-${source}` : ''} ${className}`} style={style}>
      {source && <ProjectorSource kind={source} />}
      <div className="as-glass">
        {scene && <Scene name={scene} />}
        {children}
      </div>
      {label && <span className="as-frame-label">{label}</span>}
    </div>
  );
}

// Frontal elevation of the source. A single lens lights every corner, so the
// cone silhouette runs from the lens to the far corners of the image — the
// bar below throws up, the pendant and the mount throw down. Glass insets for
// each source live in ambient.css (.as-src-*).
function ProjectorSource({ kind }) {
  const cone = (lens, a, b) => (
    <polygon points={`${lens.join(',')} ${a.join(',')} ${b.join(',')}`} fill={`url(#as-cone-${kind})`} />
  );
  const grad = (y1, y2) => (
    <linearGradient id={`as-cone-${kind}`} x1="0" y1={y1} x2="0" y2={y2}>
      <stop offset="0" stopColor="#fff2d6" stopOpacity="0.2" /><stop offset="0.5" stopColor="#fff2d6" stopOpacity="0.06" /><stop offset="1" stopColor="#fff2d6" stopOpacity="0.02" />
    </linearGradient>
  );
  let body = null;
  if (kind === 'beam') {
    body = (
      <>
        <defs>{grad(1, 0)}</defs>
        {cone([520, 500], [70, 30], [970, 30])}
        <rect x="70" y="516" width="900" height="2" fill="rgba(255,255,255,0.08)" />
        <rect x="400" y="500" width="240" height="18" rx="4" fill="#15151a" stroke="#2a2a31" />
        <rect x="414" y="507" width="212" height="2" rx="1" fill="#14e0c8" opacity="0.8" />
        <circle cx="520" cy="500" r="3" fill="#fff2d6" /><text x="520" y="548" className="as-src-label" textAnchor="middle">beam · ultra-short-throw bar · lens 0.45 m from the wall · optical axis 60° up</text>
      </>
    );
  } else if (kind === 'pendant') {
    body = (
      <>
        <defs>{grad(0, 1)}<radialGradient id="as-globe-pendant"><stop offset="0" stopColor="#fff2d6" stopOpacity="0.5" /><stop offset="1" stopColor="#fff2d6" stopOpacity="0" /></radialGradient></defs>
        {cone([534, 58], [90, 545], [950, 545])}
        {/* cord → E27 base → finned projector module (lens on the side, gimballed) → ordinary globe, lit */}
        <line x1="520" y1="0" x2="520" y2="22" stroke="#26262c" strokeWidth="2" />
        <rect x="513" y="22" width="14" height="12" fill="#6b5a3a" />
        <rect x="506" y="34" width="28" height="30" rx="4" fill="#15151a" stroke="#2a2a31" />
        <path d="M510 40 h20 M510 46 h20 M510 52 h20" stroke="#2a2a31" />
        <rect x="506" y="60" width="28" height="2" fill="#14e0c8" opacity="0.8" />
        <g transform="rotate(-18 534 58)"><rect x="530" y="52" width="16" height="12" rx="3" fill="#1d1d23" stroke="#3a3a44" /><circle cx="546" cy="58" r="3.5" fill="#0b0b0e" stroke="#9aa0ad" /></g>
        <circle cx="534" cy="58" r="2.5" fill="#fff2d6" />
        <circle cx="520" cy="84" r="30" fill="url(#as-globe-pendant)" />
        <circle cx="520" cy="84" r="17" fill="#fff6e4" opacity="0.92" />
        <text x="520" y="124" className="as-src-label" textAnchor="middle">dual-purpose bulb · globe lights the table · projector module in the neck, lens gimballed toward the wall · axis 18° below horizontal</text>
      </>
    );
  } else if (kind === 'mount') {
    body = (
      <>
        <defs>{grad(0, 1)}</defs>
        {cone([520, 66], [80, 545], [960, 545])}
        <rect x="514" y="0" width="12" height="40" fill="#26262c" />
        <rect x="462" y="40" width="116" height="26" rx="4" fill="#15151a" stroke="#2a2a31" />
        <rect x="474" y="50" width="92" height="2" fill="#14e0c8" opacity="0.8" />
        <circle cx="520" cy="66" r="3" fill="#fff2d6" />
        <text x="520" y="86" className="as-src-label" textAnchor="middle">ceiling-mounted beam · 2.4 m from the wall · 23° down · lens shift</text>
      </>
    );
  } else {
    body = (
      <>
        <defs>{grad(1, 0)}<radialGradient id="as-globe-lamp"><stop offset="0" stopColor="#fff2d6" stopOpacity="0.35" /><stop offset="1" stopColor="#fff2d6" stopOpacity="0" /></radialGradient></defs>
        {cone([126, 470], [0, 0], [1040, 0])}
        {/* nightstand → ceramic socket → E27 base → finned module with a lens barrel on its shoulder, aimed up and into the room → dim globe on top */}
        <rect x="40" y="560" width="160" height="25" fill="#1a1612" stroke="#2f2a24" />
        <path d="M96 560 h28 l-4 -22 h-20 z" fill="#2a2520" stroke="#3a342c" />
        <rect x="104" y="528" width="12" height="10" fill="#6b5a3a" />
        <rect x="98" y="496" width="24" height="32" rx="4" fill="#15151a" stroke="#2a2a31" />
        <path d="M102 504 h16 M102 511 h16 M102 518 h16" stroke="#2a2a31" />
        <rect x="98" y="500" width="24" height="2" fill="#14e0c8" opacity="0.8" />
        <g transform="rotate(34 120 482)"><rect x="113" y="470" width="14" height="18" rx="3" fill="#1d1d23" stroke="#3a3a44" /><circle cx="120" cy="470" r="3.5" fill="#0b0b0e" stroke="#9aa0ad" /></g>
        <circle cx="126" cy="470" r="2.5" fill="#fff2d6" />
        <circle cx="106" cy="478" r="26" fill="url(#as-globe-lamp)" />
        <circle cx="106" cy="478" r="15" fill="#f3e2c4" opacity="0.7" />
        <text x="210" y="520" className="as-src-label">dual-purpose bulb · globe is the night light · projector module's lens on its shoulder, axis 34° off vertical toward the room</text>
      </>
    );
  }
  // Stretch to the frame box so the cone meets the glass insets (set in px)
  // even when a frame is narrower than the 1040 canvas.
  return <svg className="as-src" viewBox="0 0 1040 585" preserveAspectRatio="none" aria-hidden="true">{body}</svg>;
}

// Side elevation of the throw: room section in metres, the lens, the optical
// axis with its angle, the cone, the image on the wall or ceiling, and the
// numbers. Design targets, not measurements.
const THROWS = {
  beam: {
    title: 'Beam on the credenza', surface: 'wall', ceiling: 2.6, lens: [0.45, 0.8], img: [0.95, 2.2],
    furniture: [[0.1, 0, 0.5, 0.75]], unit: [0.35, 0.75, 0.2, 0.07], ref: 'h',
    facts: [['Image', '100in · 2.21 × 1.25 m'], ['Lens to wall', '0.45 m · throw ratio 0.2'], ['Optical axis', '60° above horizontal · via UST mirror'], ['Offset', 'image bottom 0.15 m above the bar · designed in, no keystone'], ['Reads', 'shaded room by day · any evening']],
  },
  pendant: {
    title: 'Bulb in the pendant', surface: 'wall', ceiling: 2.6, lens: [1.4, 2.0], img: [1.12, 1.99],
    furniture: [[0.9, 0, 1.0, 0.75]], cord: [1.4, 2.6, 2.08], globe: [1.4, 1.86], ref: 'v',
    facts: [['Image', '70in · 1.55 × 0.87 m'], ['Lens to wall', '1.4 m · throw ratio 0.9'], ['Bulb', 'E27 base → projector module in the neck → ordinary globe; the globe lights the table'], ['Gimbal', 'side lens, 72° from the socket axis · axis 18° below horizontal'], ['Keystone', '18° vertical · corrected in the engine'], ['Reads', 'evening and dim rooms · 300–700 lm class']],
  },
  lamp: {
    title: 'Bulb in the bedside lamp', surface: 'ceiling', ceiling: 2.5, lens: [0.37, 0.75], img: [0.34, 2.77],
    furniture: [[0.1, 0, 0.5, 0.6], [0.7, 0, 2.2, 0.55]], lampBody: [0.3, 0.6, 0.1, 0.15], globe: [0.3, 0.88], ref: 'v',
    facts: [['Image', '110in · 2.43 × 1.37 m on the ceiling'], ['Lens to ceiling', '1.75 m up · 1.2 m across · throw 2.12 m · ratio 0.87'], ['Bulb', 'bare in the socket: base → module → globe; lens barrel on the module shoulder, beside the globe'], ['Axis', '34° off vertical toward the room'], ['Keystone', '34° · corrected · ~25% of the panel spent'], ['Reads', 'night only · 60–160in by throw · globe dimmed to a night light']],
  },
  mount: {
    title: 'Beam on a ceiling mount', surface: 'wall', ceiling: 2.7, lens: [2.4, 2.62], img: [0.96, 2.45],
    furniture: [[0.4, 0, 1.2, 0.72]], unit: [2.25, 2.62, 0.3, 0.08], bracket: [2.4, 2.7, 2.7], ref: 'h',
    facts: [['Image', '120in · 2.66 × 1.49 m'], ['Lens to wall', '2.4 m · throw ratio 0.9 · standard-throw optics'], ['Optical axis', '23° below horizontal'], ['Keystone', 'lens shift first, 23° corrected after'], ['Reads', 'shaded room by day · 1,500–2,500 lm class']],
  },
};

export function ThrowDiagram({ kind, w = 1040, h = 176 }) {
  const t = THROWS[kind] || THROWS.beam;
  const S = 52; const ox = 60; const floorY = h - 18;
  const X = (m) => ox + m * S; const Y = (m) => floorY - m * S;
  const onWall = t.surface === 'wall';
  const imgA = onWall ? [X(0), Y(t.img[0])] : [X(t.img[0]), Y(t.ceiling)];
  const imgB = onWall ? [X(0), Y(t.img[1])] : [X(t.img[1]), Y(t.ceiling)];
  const lens = [X(t.lens[0]), Y(t.lens[1])];
  const mid = [(imgA[0] + imgB[0]) / 2, (imgA[1] + imgB[1]) / 2];
  const horiz = Math.atan2(Math.abs(lens[1] - mid[1]), Math.abs(mid[0] - lens[0])) * 180 / Math.PI; // from horizontal, 0..90
  const dir = Math.atan2(mid[1] - lens[1], mid[0] - lens[0]) * 180 / Math.PI; // SVG rotation of the lens glyph
  const up = mid[1] < lens[1];
  const roomW = 3.4; const diagW = X(roomW) + 20;
  const refEnd = t.ref === 'h' ? [lens[0] - 40, lens[1]] : [lens[0], lens[1] + (onWall ? 40 : -40)];
  const label = t.ref === 'h' ? `${Math.round(horiz)}° ${up ? 'up' : 'down'}` : `${Math.round(90 - horiz)}° from vertical`;
  return (
    <div className="as-throw">
      <svg viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
        <defs><linearGradient id={`as-throw-${kind}`} x1={lens[0]} y1={lens[1]} x2={mid[0]} y2={mid[1]} gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#fff2d6" stopOpacity="0.35" /><stop offset="1" stopColor="#fff2d6" stopOpacity="0.06" /></linearGradient></defs>
        <rect x={ox} y={Y(t.ceiling)} width={X(roomW) - ox} height={floorY - Y(t.ceiling)} fill="rgba(255,255,255,0.025)" />
        <line x1={ox} y1={Y(t.ceiling)} x2={ox} y2={floorY} stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
        <line x1={ox} y1={floorY} x2={X(roomW)} y2={floorY} stroke="rgba(255,255,255,0.35)" />
        <line x1={ox} y1={Y(t.ceiling)} x2={X(roomW)} y2={Y(t.ceiling)} stroke="rgba(255,255,255,0.35)" />
        {t.furniture.map(([x, y, fw, fh], i) => <rect key={i} x={X(x)} y={Y(y + fh)} width={fw * S} height={fh * S} fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.18)" />)}
        {t.lampBody && <rect x={X(t.lampBody[0])} y={Y(t.lampBody[1] + t.lampBody[3])} width={t.lampBody[2] * S} height={t.lampBody[3] * S} fill="#1a1612" stroke="rgba(255,255,255,0.3)" />}
        {t.cord && <line x1={X(t.cord[0])} y1={Y(t.cord[1])} x2={X(t.cord[0])} y2={Y(t.cord[2])} stroke="rgba(255,255,255,0.4)" />}
        {t.bracket && <line x1={X(t.bracket[0])} y1={Y(t.bracket[1])} x2={X(t.bracket[0])} y2={Y(t.bracket[1]) + 4} stroke="rgba(255,255,255,0.4)" strokeWidth="3" />}
        <polygon points={`${lens.join(',')} ${imgA.join(',')} ${imgB.join(',')}`} fill={`url(#as-throw-${kind})`} />
        <line x1={imgA[0]} y1={imgA[1]} x2={imgB[0]} y2={imgB[1]} stroke="#14e0c8" strokeWidth="4" strokeLinecap="round" />
        <line x1={lens[0]} y1={lens[1]} x2={mid[0]} y2={mid[1]} stroke="#fff2d6" strokeDasharray="3 4" />
        <line x1={lens[0]} y1={lens[1]} x2={refEnd[0]} y2={refEnd[1]} stroke="rgba(255,255,255,0.4)" strokeDasharray="2 3" />
        {t.unit && <rect x={X(t.unit[0])} y={Y(t.unit[1] + t.unit[3])} width={t.unit[2] * S} height={t.unit[3] * S} rx="2" fill="#15151a" stroke="#14e0c8" />}
        {!t.unit && <g transform={`rotate(${dir} ${lens[0]} ${lens[1]})`}><rect x={lens[0] - 14} y={lens[1] - 5} width="14" height="10" rx="2" fill="#15151a" stroke="#14e0c8" /></g>}
        {t.globe && <circle cx={X(t.globe[0])} cy={Y(t.globe[1])} r="6" fill="#fff6e4" opacity="0.85" />}
        {t.globe && <text x={X(t.globe[0]) + 10} y={Y(t.globe[1]) + 4} className="as-svg-tick">globe · lamp</text>}
        <circle cx={lens[0]} cy={lens[1]} r="3" fill="#fff2d6" />
        <text x={lens[0] + (t.ref === 'h' ? -46 : 10)} y={lens[1] + (t.ref === 'h' ? -8 : onWall ? 30 : -30)} className="as-svg-tick">{label}</text>
        <text x={onWall ? ox - 8 : mid[0]} y={onWall ? mid[1] : Y(t.ceiling) - 6} className="as-svg-tick" textAnchor={onWall ? 'end' : 'middle'} transform={onWall ? `rotate(-90 ${ox - 8} ${mid[1]})` : undefined}>image</text>
        <text x={ox} y={h - 4} className="as-svg-tick">0 m</text><text x={X(1)} y={h - 4} className="as-svg-tick">1 m</text><text x={X(2)} y={h - 4} className="as-svg-tick">2 m</text><text x={X(3)} y={h - 4} className="as-svg-tick">3 m</text>
        <text x={ox + 6} y={Y(t.ceiling) + 12} className="as-svg-tick">ceiling {t.ceiling} m</text>
        <foreignObject x={diagW + 16} y="8" width={w - diagW - 24} height={h - 16}>
          <div className="as-throw-facts" xmlns="http://www.w3.org/1999/xhtml">
            <strong>{t.title} · section</strong>
            {t.facts.map(([k, v]) => <div key={k}><span>{k}</span><em>{v}</em></div>)}
            <small>design targets to be benched · the arrow is the optical axis, the band is the image</small>
          </div>
        </foreignObject>
      </svg>
    </div>
  );
}

// Loop artwork lives in public/ (not bundled); `gallery` is a dark room — the
// print is drawn by <Painting>.
const SCENE_ART = ['ocean', 'dusk', 'dawn', 'night', 'fjord', 'mist', 'garden', 'art', 'lisbon', 'living'];
const art = (name) => `url(/concepts/ambient-screen/scenes/${name}.jpg)`;

export function Scene({ name, style }) {
  const img = SCENE_ART.includes(name) ? { backgroundImage: art(name) } : null;
  return <div className={`as-scene as-scene-${name}`} style={{ ...img, ...style }} aria-hidden="true" />;
}

export const pos = (left, top, width, height) => ({ position: 'absolute', left, top, width, height });

export function Card({ title, right, children, className = '', style, tone }) {
  return (
    <div className={`as-card ${tone ? `tone-${tone}` : ''} ${className}`} style={style}>
      {(title || right) && (
        <div className="as-card-head">
          {title && <span className="as-h">{title}</span>}
          {right && <span className="as-card-right">{right}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

// One dense list line: main · sub · right, optional tone on the right.
export function Line({ a, b, c, tone, on, lead }) {
  return (
    <div className={`as-line ${on ? 'on' : ''}`}>
      {lead}
      <span className="as-line-a">{a}</span>
      {b && <span className="as-line-b">{b}</span>}
      {c != null && <span className={`as-line-c ${tone ? `tone-${tone}` : ''}`}>{c}</span>}
    </div>
  );
}

export function Chip({ children, tone = '', className = '' }) {
  return <span className={`as-chip ${tone ? `tone-${tone}` : ''} ${className}`}>{children}</span>;
}

export function Bar({ v, color = COLORS.teal, h = 4 }) {
  return (
    <span className="as-bar" style={{ height: h }}>
      <span style={{ width: `${Math.round(Math.max(0, Math.min(1, v)) * 100)}%`, background: color }} />
    </span>
  );
}

export function Clock({ time, date, size = 40, className = '' }) {
  return (
    <div className={`as-clock ${className}`}>
      <span className="as-clock-time" style={{ fontSize: size }}>{time}</span>
      {date && <span className="as-clock-date">{date}</span>}
    </div>
  );
}

const AV = ['#14e0c8', '#8b7cff', '#f5a623', '#38bdf8', '#e5484d', '#3ecf8e'];
export function Avatar({ name, i = 0, size = 20 }) {
  return (
    <span className="as-avatar" style={{ width: size, height: size, fontSize: size * 0.48, background: AV[i % AV.length] }}>
      {name.slice(0, 1)}
    </span>
  );
}

export function Spark({ points, color = COLORS.teal, width = 72, height = 20 }) {
  return <Sparkline points={points} color={color} width={width} height={height} />;
}

// Tiny weather glyphs — drawn, so they stay crisp and consistent.
export function Wx({ k, size = 16 }) {
  const s = size;
  if (k === 'sun') return <svg width={s} height={s} viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="3.4" fill="#f5c76a" /><g stroke="#f5c76a" strokeWidth="1.1">{[0, 45, 90, 135, 180, 225, 270, 315].map((a) => <line key={a} x1="8" y1="1.5" x2="8" y2="3" transform={`rotate(${a} 8 8)`} />)}</g></svg>;
  if (k === 'rain') return <svg width={s} height={s} viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 9.5a2.5 2.5 0 0 1 .4-5 3.5 3.5 0 0 1 6.6 1 2 2 0 0 1-.3 4z" fill="#b9c3d1" /><g stroke="#6fb3ff" strokeWidth="1.1" strokeLinecap="round"><line x1="5.5" y1="11" x2="4.8" y2="13.5" /><line x1="8.5" y1="11" x2="7.8" y2="13.5" /><line x1="11.5" y1="11" x2="10.8" y2="13.5" /></g></svg>;
  if (k === 'moon') return <svg width={s} height={s} viewBox="0 0 16 16" aria-hidden="true"><path d="M10.5 2.5a5.5 5.5 0 1 0 3 10 6 6 0 0 1-3-10z" fill="#e8e4d4" /></svg>;
  return <svg width={s} height={s} viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 11.5a2.8 2.8 0 0 1 .4-5.6 3.8 3.8 0 0 1 7.2 1.1 2.3 2.3 0 0 1-.4 4.5z" fill="#c7ced8" /></svg>;
}

// hours: [[label, kind, temp]]
export function WeatherStrip({ hours, light }) {
  return (
    <div className={`as-wx ${light ? 'light' : ''}`}>
      {hours.map(([h, k, t]) => (
        <div key={h} className="as-wx-h">
          <span>{h}</span>
          <Wx k={k} />
          <strong>{t}°</strong>
        </div>
      ))}
    </div>
  );
}

// Deterministic pseudo-random so the sky is the same on every render.
const rnd = (seed) => { let s = seed; return () => { s = (s * 16807) % 2147483647; return s / 2147483647; }; };

export function Stars({ w = 1040, h = 585, n = 220, seed = 7, lines = true, style }) {
  const r = rnd(seed);
  const stars = Array.from({ length: n }, () => [r() * w, r() * h, 0.4 + r() * 1.3, 0.35 + r() * 0.65]);
  // Cassiopeia-ish W and an Orion-ish belt + shoulders, placed in canvas space.
  const cas = [[160, 120], [210, 90], [262, 118], [312, 84], [370, 112]];
  const ori = [[700, 180], [760, 150], [820, 190], [730, 290], [760, 300], [790, 310], [700, 400], [830, 420]];
  const oriLines = [[0, 3], [1, 3], [1, 5], [2, 5], [3, 4], [4, 5], [3, 6], [5, 7]];
  return (
    <svg className="as-stars" viewBox={`0 0 ${w} ${h}`} style={style} aria-hidden="true">
      {stars.map(([x, y, rr, o], i) => <circle key={i} cx={x} cy={y} r={rr} fill="#fff" opacity={o} />)}
      {lines && (
        <g stroke="rgba(255,255,255,0.35)" strokeWidth="0.8" fill="#fff">
          <polyline points={cas.map((p) => p.join(',')).join(' ')} fill="none" />
          {cas.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2.2" />)}
          {oriLines.map(([a, b], i) => <line key={i} x1={ori[a][0]} y1={ori[a][1]} x2={ori[b][0]} y2={ori[b][1]} />)}
          {ori.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={i === 3 || i === 4 || i === 5 ? 2 : 2.6} />)}
          <text x={cas[0][0] - 6} y={cas[0][1] + 24} className="as-stars-label">Cassiopeia</text>
          <text x={ori[6][0] - 10} y={ori[6][1] + 24} className="as-stars-label">Orion</text>
        </g>
      )}
    </svg>
  );
}

// Day arc with sun position: rise/set/now as fractions of the day.
export function SunArc({ rise = 0.31, set = 0.78, now = 0.39, w = 220, h = 70, light }) {
  const x = (f) => 10 + f * (w - 20);
  const y = (f) => {
    const t = (f - rise) / (set - rise);
    return h - 14 - Math.sin(Math.max(0, Math.min(1, t)) * Math.PI) * (h - 26);
  };
  const path = Array.from({ length: 25 }, (_, i) => rise + (i / 24) * (set - rise)).map((f, i) => `${i ? 'L' : 'M'}${x(f)} ${y(f)}`).join(' ');
  const c = light ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.3)';
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 'auto', display: 'block' }} aria-hidden="true">
      <line x1="6" y1={h - 14} x2={w - 6} y2={h - 14} stroke={c} strokeDasharray="2 3" />
      <path d={path} fill="none" stroke={c} strokeWidth="1.2" />
      <circle cx={x(now)} cy={y(now)} r="5" fill="#f5c76a" />
      <circle cx={x(now)} cy={y(now)} r="9" fill="#f5c76a" opacity="0.25" />
      <text x={x(rise)} y={h - 2} className="as-svg-tick" textAnchor="middle">07:31</text>
      <text x={x(set)} y={h - 2} className="as-svg-tick" textAnchor="middle">18:42</text>
    </svg>
  );
}

export function Moon({ phase = 0.82, size = 44 }) {
  const r = size / 2 - 2;
  const k = Math.cos(phase * 2 * Math.PI); // -1 full … 1 new
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} fill="#e8e4d4" />
      <ellipse cx={size / 2 + k * r * 0.5} cy={size / 2} rx={Math.abs(k) * r} ry={r} fill="#0b0b0f" opacity="0.92" />
    </svg>
  );
}

// Rain radar: range rings over a faint map, two soft cells drifting east.
export function Radar({ w = 200, h = 120 }) {
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 'auto', display: 'block' }} aria-hidden="true">
      <defs>
        <radialGradient id="as-rad-a"><stop offset="0" stopColor="#6fb3ff" stopOpacity="0.75" /><stop offset="1" stopColor="#6fb3ff" stopOpacity="0" /></radialGradient>
        <radialGradient id="as-rad-b"><stop offset="0" stopColor="#2f6fe0" stopOpacity="0.8" /><stop offset="1" stopColor="#2f6fe0" stopOpacity="0" /></radialGradient>
      </defs>
      <rect width={w} height={h} fill="rgba(255,255,255,0.03)" />
      {[0.2, 0.42, 0.64].map((f) => <circle key={f} cx={w * 0.5} cy={h * 0.5} r={f * w * 0.5} fill="none" stroke="rgba(255,255,255,0.14)" />)}
      <path d={`M0 ${h * 0.7} Q ${w * 0.3} ${h * 0.55} ${w * 0.55} ${h * 0.75} T ${w} ${h * 0.6}`} fill="none" stroke="rgba(255,255,255,0.12)" />
      <ellipse cx={w * 0.22} cy={h * 0.38} rx={w * 0.2} ry={h * 0.28} fill="url(#as-rad-a)" />
      <ellipse cx={w * 0.36} cy={h * 0.5} rx={w * 0.12} ry={h * 0.2} fill="url(#as-rad-b)" />
      <circle cx={w * 0.5} cy={h * 0.5} r="2.5" fill="#14e0c8" />
      <text x={w - 4} y={h - 5} className="as-svg-tick" textAnchor="end">rain reaches you ~14:50</text>
    </svg>
  );
}

export function Waveform({ w = 320, h = 26, seed = 3 }) {
  const r = rnd(seed);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      {Array.from({ length: 64 }, (_, i) => { const a = 3 + r() * (h - 6); return <rect key={i} x={i * (w / 64)} y={(h - a) / 2} width={w / 64 - 1.5} height={a} rx="1" fill="rgba(255,255,255,0.55)" />; })}
    </svg>
  );
}

export function ScoreBand({ light }) {
  return (
    <div className={`as-score ${light ? 'light' : ''}`}>
      <span className="as-score-live">LIVE</span>
      <strong>ARS</strong><span className="as-score-n">1</span>
      <span className="as-score-sep">–</span>
      <span className="as-score-n">1</span><strong>LIV</strong>
      <span className="as-score-min">71'</span>
      <span className="as-score-ev">63' Saka · 58' Díaz</span>
    </div>
  );
}

// A framed painting stand-in for the gallery loop (no licensed art shipped).
export function Painting({ style }) {
  return (
    <div className="as-painting" style={style}>
      {/* the print itself is the scenes/gallery.jpg background (woodblock after Hokusai, public domain) */}
      <div className="as-painting-canvas" style={{ backgroundImage: art('gallery') }} aria-hidden="true" />
    </div>
  );
}

export { COLORS };
